'use client'

// His bag, for visitors (3 Nov). Sam: "Perhaps he needs a carrying interface, when you click on Pino - it shows a popup
// like a minecraft inventory - and shows what he is carrying, and he has a certain amount of slots - this could be a
// good user interaction", and for what visitors give him: "Free and capped". Nine places: it was eight and his beak
// until that afternoon, when "do you think we need to differentiate between his beak and his bag? I think perhaps it
// could just be whats in his bag maybe?" and "yes merge them".
//
// A click on him opens it (components/three/npc/BirdHit.jsx), and so does a click on the bubble over his head that shows
// a visitor near him the newest thing in his bag (components/three/npc/ThoughtBubble.jsx). A pop-up in the middle of the
// screen (Popup.jsx), not a drawer: Sam, 3 Nov, "I think, the drawer should be a popup instead". The nine places of his
// bag in three rows of three, an empty place drawn empty, and it opens on the newest thing, the one over his head.
// Picking a thing shows it turning, its name, its note and where and when he got it, and under that a line kept for how
// he feels about it: his mind's fondness for things fills it later (habits-plan, "Fondness kept on the thing"), and
// until then it stays empty. Below, what is on his shelves, a row for each roost's shelf by the name a visitor knows it
// by ("His shelf by the apple tree": Sam, 3 Nov, "perhaps we give multiple shelves, since he has multiple roosts now"),
// what he keeps at home on no shelf and what is lying about for him, each only when there is some, and the seven things
// a visitor can give him, one a day. The town keeps the count and says no past it (app/api/bag/give/route.js).
//
// What it costs: nothing while shut, when it renders null and asks for nothing. Open, /api/bag once and every fifteen
// seconds after, kept at the edge (app/api/bag/route.js), and the pictures, each drawn once by one small renderer of
// their own with only the picked thing turning (bagPictures.js), held while it is open and let go when it shuts.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { useAccount, authFetch } from '../auth/useUser'
import { HAS_AUTH } from '../../lib/features'
import { Card } from './Drawer'
import { Popup } from './Popup'
import { KawaiiCta } from './KawaiiButton'
import { kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import { GIFTS, BAG_SLOTS } from '../../lib/things/bag'
import { lookOfEntry, itemLook, lookKey } from '../../lib/things/looks'
import { gotLine, nextGiftAt } from '../../lib/things/bagView'
import { untilWords } from '../../lib/ui/since'
import { PICTURE_PX, canDraw, holdPictures, pictureNow, pictureOf, turn } from './bagPictures'

const POLL_MS = 15_000
// until when this browser knows a person's gift for the day is given, by username. The town holds the real count and
// refuses past it; this only lets the buttons say so before anybody presses one.
const GAVE_KEY = 'pino-bag-gave:'
const IN_BAG = 'In his bag'
const HOME = 'Kept at home'
// (the town names each shelf; one it sends with no name is still his)
const shelfTitle = (sh) => sentence(sh?.name) || 'His shelf'
const GROUND = 'Lying about for him'

const sentence = (s) => { const t = String(s || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : '' }
const bare = (s) => String(s || '').replace(/^(a|an|the|some)\s+/i, '')
const quiet = { color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.5 }
const heading = { color: 'rgba(47,38,80,0.55)', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 8 }

// A thing's picture: drawn once a visit (bagPictures.js), the same picture wherever it shows.
function usePicture(look) {
  const key = lookKey(look)
  const [got, setGot] = useState({ key: '', url: null })
  useEffect(() => {
    if (!key || pictureNow(look)) return undefined
    let dead = false
    pictureOf(look).then((url) => { if (!dead) setGot({ key, url }) })
    return () => { dead = true }
    // the key is the look: every poll brings a fresh object for the same thing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  if (!key) return null
  return pictureNow(look) || (got.key === key ? got.url : null)
}

function Picture({ look, name, size = '78%' }) {
  const url = usePicture(look)
  if (url) {
    // a picture drawn on the page as a data URL: there is nothing for next/image to fetch or shrink
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={url} alt="" draggable={false} style={{ width: size, height: size, imageRendering: 'pixelated', display: 'block', pointerEvents: 'none' }} />
  }
  // a thing with no model to draw it from (the old shop's blanket and the like), or no WebGL here: its name, small
  if (name && (!lookKey(look) || !canDraw())) return <span style={{ fontSize: 10.5, fontWeight: 700, lineHeight: 1.15, padding: 4, textAlign: 'center', overflow: 'hidden' }}>{bare(name)}</span>
  return null
}

function Place({ entry, picked, onPick }) {
  const on = !!entry && picked === entry.id
  return (
    <button
      type="button"
      disabled={!entry}
      onClick={() => entry && onPick(entry.id)}
      aria-pressed={on}
      aria-label={entry ? entry.name : 'an empty place'}
      title={entry ? sentence(entry.name) : undefined}
      style={{
        aspectRatio: '1 / 1',
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        overflow: 'hidden',
        background: kawaii.creamDeep,
        border: kawaii.outlineThin,
        borderRadius: 12,
        boxShadow: 'inset 0 3px 0 rgba(47,38,80,0.12)',
        outline: on ? '3px solid #f0b064' : 'none',
        outlineOffset: 2,
        color: kawaii.ink,
        cursor: entry ? 'pointer' : 'default',
      }}
    >
      {entry && <Picture look={lookOfEntry(entry)} name={entry.name} />}
    </button>
  )
}

// The picked thing, turning, in the one small canvas.
function Turning({ look }) {
  const ref = useRef(null)
  const key = lookKey(look)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => turn(look, ref.current), [key])
  return <canvas ref={ref} width={PICTURE_PX} height={PICTURE_PX} aria-hidden="true" style={{ width: '100%', height: '100%', imageRendering: 'pixelated', display: 'block' }} />
}

function Picked({ entry, where }) {
  if (!entry) return <p style={quiet}>Choose a thing to see it here.</p>
  const got = gotLine(entry)
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
      <div style={{ width: 'min(120px, 32vw)', aspectRatio: '1 / 1', flex: 'none', borderRadius: 14, background: kawaii.creamDeep, border: kawaii.outlineThin, boxShadow: 'inset 0 3px 0 rgba(47,38,80,0.12)' }}>
        <Turning look={lookOfEntry(entry)} />
      </div>
      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <div style={heading}>{where}</div>
        <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25 }}>{sentence(entry.name)}</div>
        {entry.note && <div style={{ ...quiet, color: kawaii.ink, marginTop: 4 }}>{sentence(entry.note)}</div>}
        {got && <div style={{ ...quiet, marginTop: 4 }}>{sentence(got)}</div>}
        {/* how he feels about it: his mind's fondness for the thing says this later, and nothing does until then */}
        <div style={{ ...quiet, minHeight: 19, marginTop: 2 }} />
      </div>
    </div>
  )
}

function Row({ title, entries, picked, onPick }) {
  if (!entries.length) return null
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={heading}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {entries.map((e) => (
          <div key={e.id} style={{ width: 52 }}>
            <Place entry={e} picked={picked} onPick={onPick} />
          </div>
        ))}
      </div>
    </div>
  )
}

// The seven things a visitor can give him (lib/things/bag.js GIFTS), one a day.
function Give({ onGiven, onConnect }) {
  const { signedIn, username } = useAccount()
  const pushToast = useGameStore((s) => s.pushToast)
  const [pick, setPick] = useState(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState('')
  const [until, setUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let at = 0
    if (username) { try { at = Number(window.localStorage.getItem(GAVE_KEY + username)) || 0 } catch { /* private mode */ } }
    setUntil(at)
    setNow(Date.now())
  }, [username])
  // once a minute while it is shut, so the buttons come back on the minute it opens
  useEffect(() => {
    if (!until) return undefined
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [until])

  const spent = until > now
  const can = signedIn && !spent && !busy
  const gift = GIFTS.find((g) => g.id === pick) || null

  const remember = (at) => {
    setUntil(at)
    setNow(Date.now())
    try { window.localStorage.setItem(GAVE_KEY + username, String(at)) } catch { /* private mode */ }
  }

  const give = async () => {
    if (!gift || !can) return
    setBusy(true)
    setSaid('')
    try {
      const r = await authFetch('/api/bag/give', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ giftId: gift.id }) })
      const d = await r.json().catch(() => ({}))
      if (r.ok) {
        remember(nextGiftAt())
        setPick(null)
        pushToast("It's on the ground near him.", true)
        onGiven?.()
      } else {
        // over the day's one, the town says when the next opens; with the ground full it does not, and the buttons stay
        if (r.status === 429 && Number(d.opensAt) > Date.now()) remember(Number(d.opensAt))
        const why = d.error || "That didn't go through. Try again in a minute."
        setSaid(why)
        pushToast(why, false)
      }
    } catch {
      setSaid("That didn't go through. Try again in a minute.")
    }
    setBusy(false)
  }

  if (!HAS_AUTH) return null
  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 800 }}>Give him something</div>
      <p style={{ ...quiet, margin: '2px 0 12px' }}>One a day. It lands near him and he finds it.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        {GIFTS.map((g) => {
          const on = pick === g.id
          return (
            <button
              key={g.id}
              type="button"
              disabled={!can}
              onClick={() => setPick((p) => (p === g.id ? null : g.id))}
              aria-pressed={on}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0,
                padding: '8px 4px 7px',
                background: on ? '#f6d3a1' : kawaii.creamDeep,
                border: kawaii.outlineThin,
                borderRadius: 12,
                outline: on ? '3px solid #f0b064' : 'none',
                outlineOffset: 2,
                color: kawaii.ink,
                cursor: can ? 'pointer' : 'default',
                opacity: can ? 1 : 0.6,
              }}
            >
              <span style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Picture look={itemLook(g.itemId, g.spec)} size={44} />
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>{bare(g.name)}</span>
            </button>
          )
        })}
      </div>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {!signedIn ? (
          <>
            <span style={quiet}>Connect to give him something.</span>
            <KawaiiCta intent="primary" small onClick={onConnect}>connect to {BIRD_NAME}</KawaiiCta>
          </>
        ) : spent ? (
          <>
            <KawaiiCta intent="primary" small disabled>Your next one opens in {untilWords(until - now)}</KawaiiCta>
            <span style={quiet}>You've given him something today.</span>
          </>
        ) : (
          <KawaiiCta intent="primary" small disabled={!gift || busy} onClick={give}>
            {busy ? 'Giving…' : gift ? `Give him ${gift.name}` : 'Pick something to give him'}
          </KawaiiCta>
        )}
      </div>
      {said && !spent && <p style={{ ...quiet, color: '#a83832', marginTop: 10 }}>{said}</p>}
    </Card>
  )
}

// A tap on him opens this as the finger lifts, and a phone sends the tap's click a moment later to whatever is under
// the finger by then: the dim world round the pop-up, which closes it. So for a moment after opening, nothing closes
// it.
const SETTLE_MS = 450

function OpenBag() {
  const setOpen = useGameStore((s) => s.setBagOpen)
  // (when it first rendered, not when its effects ran: the click can come between the two)
  const [openedAt] = useState(() => performance.now())
  const close = useCallback(() => {
    if (performance.now() - openedAt < SETTLE_MS) return
    setOpen(false)
  }, [setOpen, openedAt])
  const [view, setView] = useState(null) // null while it is first read, { dark: true } when the town is not answering
  const [picked, setPicked] = useState(null)
  const read = useRef(() => {})

  // on opening, then every fifteen seconds while it is open and in sight
  useEffect(() => {
    let dead = false
    read.current = () => {
      if (document.hidden) return
      fetch('/api/bag')
        .then((r) => (r.ok ? r.json() : { dark: true }))
        .catch(() => ({ dark: true }))
        // (a read that fails keeps what was last there rather than emptying the bag in front of somebody)
        .then((v) => { if (!dead) setView((prev) => (v?.dark && prev && !prev.dark ? prev : v)) })
    }
    read.current()
    const t = setInterval(() => read.current(), POLL_MS)
    return () => { dead = true; clearInterval(t); read.current = () => {} }
  }, [])
  // the pictures' renderer is kept while it is open, and goes when it shuts unless the bubble over his head is drawing
  useEffect(() => holdPictures(), [])

  const ready = view && !view.dark
  const places = useMemo(() => {
    if (!ready) return []
    return [
      ...(view.bag || []).filter(Boolean).map((entry) => ({ entry, where: IN_BAG })),
      ...(view.shelves || []).flatMap((sh) => (sh.things || []).map((entry) => ({ entry, where: shelfTitle(sh) }))),
      ...(view.home || []).map((entry) => ({ entry, where: HOME })),
      ...(view.ground || []).map((entry) => ({ entry, where: GROUND })),
    ]
  }, [ready, view])
  // what is picked, or else the newest thing in his bag (the one over his head), or else the first
  const chosen = places.find((p) => p.entry.id === picked)
    || places.find((p) => p.where === IN_BAG && p.entry.id === view?.newest)
    || places.find((p) => p.where === IN_BAG) || null
  const bag = Array.from({ length: BAG_SLOTS }, (_, i) => (ready ? view.bag?.[i] || null : null))
  const room = bag.filter((e) => !e).length

  const connect = () => {
    const s = useGameStore.getState()
    s.setConnectThen('bag')
    s.setBagOpen(false)
    s.setProfileOpen(true)
  }

  return (
    <Popup onClose={close} title={`${BIRD_NAME}'s bag`} subtitle="What he has with him. His bag has nine places.">
      <div className="grid gap-4 md:grid-cols-2 md:gap-x-6">
        <div className="min-w-0">
          <Card style={{ padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
              {bag.map((e, i) => <Place key={e?.id || `empty-${i}`} entry={e} picked={chosen?.entry.id} onPick={setPicked} />)}
            </div>
            <p style={{ ...quiet, marginTop: 10 }}>
              {!view ? 'Looking in his bag…'
                : view.dark ? "His bag can't be seen right now. Try again in a minute."
                  : room === BAG_SLOTS ? 'His bag is empty.'
                    : room === 0 ? 'His bag is full.'
                      : `Room for ${room} more ${room === 1 ? 'thing' : 'things'}.`}
            </p>
          </Card>
          {ready && (
            <Card style={{ marginTop: 12 }}>
              <Picked entry={chosen?.entry || null} where={chosen?.where} />
            </Card>
          )}
        </div>
        <div className="min-w-0">
          {ready && (view.shelves || []).map((sh) => <Row key={sh.nest || sh.name} title={shelfTitle(sh)} entries={sh.things || []} picked={chosen?.entry.id} onPick={setPicked} />)}
          {ready && <Row title={HOME} entries={view.home || []} picked={chosen?.entry.id} onPick={setPicked} />}
          {ready && <Row title={GROUND} entries={view.ground || []} picked={chosen?.entry.id} onPick={setPicked} />}
          <Give onGiven={() => read.current()} onConnect={connect} />
        </div>
      </div>
    </Popup>
  )
}

/** His bag's pop-up. Nothing at all while it is shut. */
export function BagPanel() {
  const open = useGameStore((s) => s.bagOpen)
  return open ? <OpenBag /> : null
}

export default BagPanel
